import { createReadOnlyMarketError } from '@/features/market/readOnlyMarket';
import { useWritableMarketPreflight } from '@/features/market/useWritableMarketPreflight';
import type { UseMutationResult } from '@tanstack/react-query';
import * as React from 'react';

export function useWritableMarketMutation<
  TData,
  TError = Error,
  TVariables = void,
  TContext = unknown,
>(mutation: UseMutationResult<TData, TError, TVariables, TContext>) {
  const ensureWritableMarket = useWritableMarketPreflight();

  const mutate = React.useCallback<UseMutationResult<TData, TError, TVariables, TContext>['mutate']>(
    (variables, options) => {
      void (async () => {
        if (!(await ensureWritableMarket())) {
          return;
        }

        mutation.mutate(variables, options);
      })();
    },
    [ensureWritableMarket, mutation]
  );

  const mutateAsync = React.useCallback<
    UseMutationResult<TData, TError, TVariables, TContext>['mutateAsync']
  >(
    async (variables, options) => {
      if (!(await ensureWritableMarket())) {
        throw createReadOnlyMarketError() as TError;
      }

      return mutation.mutateAsync(variables, options);
    },
    [ensureWritableMarket, mutation]
  );

  return React.useMemo(
    () => ({
      ...mutation,
      mutate,
      mutateAsync,
    }),
    [mutate, mutateAsync, mutation]
  );
}
